/* Профиль тренера — как в прототипе (profile.html): три таба, таб — в хэше адреса (#billing — «Подписка», #partner — «Партнёрская программа»).
   «Профиль»: как вас видят клиенты, ссылка-приглашение, реквизиты для оплаты занятий, вход в кабинет.
   «Подписка»: сводка (тариф, клиенты, стоимость, действует до), карта, следующий платёж, история платежей.
   Оплата картой — CloudPayments (PAY-8, lib/cloudpayments.ts), когда на сервере есть ключи (me.sub.pay); без них
   «Продлить», «Оплатить» и «Привязать карту» ведут в чат поддержки, срок продлевают вручную в админке. */
import { getSession, setSession, trpc } from '@tg/app-core';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMe, type Me } from '../lib/data';
import { limitLabel, planOf, PLANS, TRIAL } from '../lib/dict';
import { dateOf, daysBetween, dlong, ini, phoneView, plural, rub, today } from '../lib/format';
import { usePageTitle } from '../shell/Shell';
import { openSupport } from '../shell/Support';
import { Modal } from '../ui/Modal';
import { toast } from '../ui/Toast';
import { InviteBlock } from '../ui/InviteLink';
import { pendingOps } from '../offline/outbox';
import { dropDb } from '../offline/idb';
import { markSignedOut } from './Login';
import { usePay } from '../lib/cloudpayments';
import { PartnerTab } from './Partner';

type F = 'first' | 'last' | 'email' | 'city' | 'about';

const TABS: [string, string, ReactNode][] = [
  [
    'profile',
    'Профиль',
    <>
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M5 19.5c1.2-3.2 3.8-5 7-5s5.8 1.8 7 5" />
    </>,
  ],
  [
    'billing',
    'Подписка',
    <>
      <rect x="3" y="5.5" width="18" height="13" rx="2" />
      <path d="M3 10h18M7 15h3" />
    </>,
  ],
  [
    'partner',
    'Партнёрская программа',
    <>
      <path d="M18.5 5.5l-13 13" />
      <circle cx="7.5" cy="7.5" r="2.5" />
      <circle cx="16.5" cy="16.5" r="2.5" />
    </>,
  ],
];
const CAMERA = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
    <circle cx="12" cy="13" r="3.5" />
  </svg>
);
const PEN = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 20h4L19 9l-4-4L4 16z" />
    <path d="M13.5 6.5l4 4" />
  </svg>
);
const dnum = (iso: string) => iso.split('-').reverse().join('.');

export function Profile() {
  usePageTitle('Профиль');
  const me = useMe().data;
  const { hash } = useLocation();
  const tab = hash === '#billing' ? 'billing' : hash === '#partner' ? 'partner' : 'profile';
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tab]);
  /* На телефоне табы не помещаются и прокручиваются вбок — открытый таб должен быть виден */
  const tabsRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = tabsRef.current;
    const on = nav?.querySelector<HTMLElement>('.on');
    if (!nav || !on) return;
    const right = on.offsetLeft - nav.offsetLeft + on.offsetWidth + 16;
    if (right > nav.clientWidth) nav.scrollLeft = right - nav.clientWidth;
    else if (on.offsetLeft - nav.offsetLeft < nav.scrollLeft) nav.scrollLeft = 0;
  }, [tab, !!me]);
  if (!me) return <div className="pagewrap"></div>;
  return (
    <div className="pagewrap">
      <div id="page">
        <nav className="ptabs" ref={tabsRef}>
          {TABS.map(([k, n, ic]) => (
            <Link key={k} to={{ hash: '#' + k }} replace className={k === tab ? 'on' : ''}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                {ic}
              </svg>
              {n}
            </Link>
          ))}
        </nav>
        <div className="prof">{tab === 'billing' ? <Billing me={me} /> : tab === 'partner' ? <PartnerTab /> : <ProfileTab me={me} />}</div>
      </div>
    </div>
  );
}

const Row = ({ label, desc, children }: { label: string; desc?: string; children: ReactNode }) => (
  <div className="srow">
    <div className="sl">
      <b>{label}</b>
      {desc && <s>{desc}</s>}
    </div>
    <div className="sc">{children}</div>
  </div>
);

/* ─── Профиль ─── */

function ProfileTab({ me }: { me: Me }) {
  const utils = trpc.useUtils();
  const upd = trpc.trainer.updateProfile.useMutation({
    onSuccess: () => utils.trainer.me.invalidate(),
    onError: (e) => toast(e.message || 'Не сохранилось'),
  });
  const save = (v: Parameters<typeof upd.mutate>[0], msg?: string) => upd.mutate(v, { onSuccess: () => msg && toast(msg) });
  const photoInput = useRef<HTMLInputElement>(null);
  /* Фото — сразу по выбору файла: квадрат 320 px из середины кадра, JPEG (как в клиентском приложении) */
  const onPhoto = (f: File | undefined) => {
    if (!f) return;
    const img = new Image();
    const url = URL.createObjectURL(f);
    img.onload = () => {
      const n = 320;
      const cv = document.createElement('canvas');
      const m = Math.min(img.width, img.height);
      cv.width = cv.height = n;
      cv.getContext('2d')!.drawImage(img, (img.width - m) / 2, (img.height - m) / 2, m, m, 0, 0, n, n);
      URL.revokeObjectURL(url);
      save({ photo: cv.toDataURL('image/jpeg', 0.85) }, 'Фото обновлено');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      toast('Не получилось открыть файл — нужна картинка JPG или PNG');
    };
    img.src = url;
  };
  const inp = (k: F, ph?: string, area?: boolean) => {
    const props = {
      className: 'inp',
      key: k + (me[k] ?? ''),
      defaultValue: me[k] ?? '',
      placeholder: ph,
      'aria-label': ph,
      onBlur: (e: { target: { value: string } }) => {
        const v = e.target.value.trim();
        if (v !== (me[k] ?? '')) save({ [k]: k === 'first' || k === 'last' ? v : v || null }, 'Сохранено');
      },
    };
    return area ? <textarea {...props} rows={3} /> : <input {...props} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()} />;
  };
  const name = [me.first, me.last].filter(Boolean).join(' ');

  return (
    <>
      <div className="sechead">
        <span className="t">Как вас видят клиенты</span>
        <span className="ln"></span>
      </div>
      <div className="card cb">
        <Row label="Фото">
          <div className="avrow">
            <label className={'av av-up' + (me.photo ? ' has' : '')} title={me.photo ? 'Сменить фото' : 'Загрузить фото'} onClick={() => photoInput.current?.click()}>
              {me.photo ? <img src={me.photo} alt="" /> : ini(name || 'Т')}
              <span className="av-cam">{CAMERA}</span>
            </label>
            <button className="btn gh sm" onClick={() => photoInput.current?.click()}>
              {me.photo ? 'Сменить фото' : 'Загрузить фото'}
            </button>
            <input ref={photoInput} type="file" accept="image/*" hidden onChange={(e) => onPhoto(e.target.files?.[0])} />
          </div>
        </Row>
        <Row label="Имя и фамилия">
          <div className="cols2">
            {inp('first', 'Имя')}
            {inp('last', 'Фамилия')}
          </div>
        </Row>
        <Row label="Город">{inp('city')}</Row>
        <Row label="О себе">{inp('about', undefined, true)}</Row>
      </div>

      <div className="sechead">
        <span className="t">Приглашение клиентов</span>
        <span className="ln"></span>
      </div>
      <div className="card cb">
        <InviteBlock />
      </div>

      <div className="sechead">
        <span className="t">Реквизиты для оплаты занятий</span>
        <span className="ln"></span>
      </div>
      <div className="card cb">
        <textarea
          className="inp"
          id="payHow"
          rows={3}
          aria-label="Реквизиты для оплаты занятий"
          placeholder="Например: перевод по номеру телефона, банк, получатель"
          key={me.payHow ?? ''}
          defaultValue={me.payHow ?? ''}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v !== (me.payHow ?? '')) save({ payHow: v || null }, 'Сохранено');
          }}
        />
        <div className="hint">Клиент видит их, когда подходит срок оплаты. Сумму и срок задаёте в карточке клиента.</div>
      </div>

      <div className="sechead">
        <span className="t">Вход в кабинет</span>
        <span className="ln"></span>
      </div>
      <div className="card cb">
        <Row label="Телефон" desc="Логин для входа — код приходит в Telegram, звонком или SMS">
          <input className="inp" value={phoneView(me.phone)} readOnly aria-label="Телефон" />
        </Row>
        <Row label="Email" desc="Чеки об оплате подписки">
          {inp('email')}
        </Row>
        <Row label="Выход" desc="Завершить сеанс на этом устройстве">
          <div className="pwrow">
            <span></span>
            <button
              className="btn gh sm"
              id="btnLogout"
              data-ro-ok
              onClick={async () => {
                const s = getSession();
                /* Правки без сети ещё на устройстве — после выхода их не будет */
                const n = pendingOps().length;
                if (n && !window.confirm(`Не отправлено на сервер: ${n}. Если выйти сейчас, эти правки пропадут. Выйти?`)) return;
                try {
                  if (s) await utils.client.auth.logout.mutate({ token: s.refresh });
                } catch {
                  /* выходим и без ответа сервера */
                }
                utils.invalidate();
                markSignedOut();
                setSession(null);
                /* Тренировки клиентов на устройстве не оставляем */
                if (s?.pid) dropDb(s.pid);
              }}
            >
              Выйти
            </button>
          </div>
        </Row>
      </div>

    </>
  );
}

/* ─── Подписка ─── */

function Billing({ me }: { me: Me }) {
  const utils = trpc.useUtils();
  const pays = trpc.trainer.payments.useQuery();
  const setSub = trpc.trainer.setSub.useMutation({ onSuccess: () => utils.trainer.me.invalidate() });
  const [modal, setModal] = useState<null | 'plan' | 'pay' | 'cancel'>(null);
  const sub = me.sub;
  const trial = sub.trial;
  /* pl — ступень следующего платежа: выбранная на после пробного или к следующему платежу (понижение), иначе текущая */
  const pl = planOf(sub.next || (trial ? 'c5' : me.plan));
  const { pay, busy } = usePay();
  const bindCard = () =>
    pay({ kind: 'card' }).then((r) => {
      if (r.ok) toast('Карта привязана');
      else if (r.msg) toast(r.msg);
    });
  const until = me.planUntil || today();
  const left = daysBetween(today(), until);
  const ended = sub.ended;
  const off = sub.canceled && !trial && !ended;
  const over = me.used > me.clientLimit;
  const hist = (pays.data || []).filter((p) => p.status === 'paid');
  /* Баланс аккаунта (партнёрская программа) — идёт в счёт следующего платежа: сначала баланс, остаток с карты */
  const bal = me.accountBalance || 0;
  const split = (price: number) => (bal ? `: ${bal >= price ? 'всё с баланса' : `${rub(bal)} с баланса, ${rub(price - bal)} с карты`}` : '');
  const cell = (k: string, v: ReactNode, cls = '') => (
    <div className={'bst' + (cls ? ' ' + cls : '')}>
      <s>{k}</s>
      <b>{v}</b>
    </div>
  );

  return (
    <>
      <div className="card bcard">
        <div className="bc-b">
          <div className="bc-h">
            <b>Подписка</b>
            {!off && (
              <button className="lnk-t" id="btnPlan" onClick={() => setModal('plan')}>
                {trial && !ended && !sub.next ? 'Выбрать тариф' : 'Изменить тариф'}
              </button>
            )}
          </div>
          {ended ? (
            <div className="bnote end">
              <span>
                <b>
                  {trial ? 'Пробный период закончился' : 'Подписка закончилась'} {dlong(until)}.
                </b>{' '}
                Кабинет открыт только для просмотра — после продления снова можно составлять тренировки и добавлять клиентов.
              </span>
              <button className="btn sm" id="btnRenew" onClick={() => setModal('pay')}>
                Продлить подписку
              </button>
            </div>
          ) : off ? (
            <div className="bnote">
              <span>
                <b>Подписка отменена.</b> Доступ сохранится до {dlong(until)}, потом кабинет станет только для просмотра.
              </span>
              <button className="btn sm" id="btnResume" onClick={() => setSub.mutate({ canceled: false }, { onSuccess: () => toast('Подписка возобновлена — следующий платёж ' + dlong(until)) })}>
                Возобновить
              </button>
            </div>
          ) : sub.fail && sub.fail.n > 0 && !trial ? (
            <div className="bnote end">
              <span>
                <b>Не получилось списать оплату с карты</b>
                {sub.fail.msg ? ` — ${sub.fail.msg}` : ''}. {sub.fail.n < 3 ? `Попробуем ещё раз завтра (попытка ${sub.fail.n} из 3), или оплатите сейчас.` : 'Автоматических попыток больше не будет — оплатите вручную.'}
              </span>
              <button className="btn sm" onClick={() => setModal('pay')}>
                Оплатить
              </button>
            </div>
          ) : trial && sub.paidUntil ? (
            <div className="bnote">
              <span>
                <b>Оплачено.</b> После пробного периода — {pl.n} до {dlong(sub.paidUntil)}.
              </span>
            </div>
          ) : trial && left <= 3 ? (
            <div className="bnote">
              <span>
                <b>Пробный период заканчивается {left <= 0 ? 'сегодня' : left === 1 ? 'завтра' : `через ${left} ${plural(left, 'день', 'дня', 'дней')}`}.</b> Оплатите сейчас — платная подписка начнётся сразу после пробного периода.
              </span>
              <button className="btn sm" onClick={() => setModal('pay')}>
                Оплатить
              </button>
            </div>
          ) : null}
          <div className="bstats">
            {cell('Текущий тариф', trial ? TRIAL.n : planOf(me.plan).n, 'pl')}
            {cell(
              'Клиентов',
              <>
                {me.used}
                <i> / {limitLabel(me.clientLimit)}</i>
              </>,
              over ? 'over' : '',
            )}
            {cell(
              'Стоимость',
              <>
                {(trial && !ended ? 0 : pl.price).toLocaleString('ru-RU')}
                <i> ₽/мес</i>
              </>,
            )}
            {cell(
              'Действует до',
              <>
                <span className={'dot ' + (left >= 0 ? (left <= 3 || off ? 'warn' : 'ok') : 'rm')}></span>
                {dnum(until)}
              </>,
            )}
          </div>
          <div className="pmbox">
            {me.card ? (
              <>
                <span className="pm-br">{me.card.split(' ')[0]}</span>
                <span className="pm-t">{me.card}</span>
                <button className="iact" id="btnCard" title="Изменить карту" aria-label="Изменить карту" disabled={busy} onClick={() => (sub.pay ? bindCard() : setModal('pay'))}>
                  {PEN}
                </button>
              </>
            ) : (
              <>
                <span className="pm-t">Карта не привязана</span>
                <button className="btn sm" id="btnCard" disabled={busy} onClick={() => (sub.pay ? bindCard() : setModal('pay'))}>
                  Привязать карту
                </button>
              </>
            )}
          </div>
          <div className="pmbox pm-bal">
            <span className="pm-br">₽</span>
            <span className="pm-t">
              {bal ? (
                <>
                  <b>{rub(bal)}</b> на балансе аккаунта — спишем в счёт следующего платежа
                </>
              ) : (
                'Баланс аккаунта пуст — пополняется из партнёрской программы'
              )}
            </span>
            <Link className="lnk-t" to={{ hash: '#partner' }} replace>
              {bal ? 'Пополнить' : 'Подробнее'}
            </Link>
          </div>
        </div>
        <div className="bc-f">
          <span>
            {ended
              ? `Продление — ${rub(pl.price)} за месяц, ${pl.n}; доступ откроется сразу`
              : trial
                ? sub.paidUntil
                  ? `Следующий платёж ${rub(pl.price)} — ${dlong(sub.paidUntil)}`
                  : sub.next
                  ? `Потом — ${rub(pl.price)} в месяц, ${pl.n}`
                  : 'Потом — тариф по числу клиентов, от 490 ₽ в месяц'
                : off
                  ? 'Автопродление выключено — списаний больше не будет'
                  : sub.pay && !sub.saved
                    ? `Оплата ${rub(pl.price)} — до ${dlong(until)}; привяжите карту, чтобы подписка продлевалась сама`
                    : `Следующий платёж ${rub(pl.price)} — ${dlong(until)}, через ${left} ${plural(left, 'день', 'дня', 'дней')}${split(pl.price)}`}
          </span>
          {!trial && !off && !ended && (
            <button className="lnk-t" id="btnCancel" onClick={() => setModal('cancel')}>
              Отменить подписку
            </button>
          )}
        </div>
      </div>

      <div className="card bcard">
        <div className="bc-b">
          <div className="bc-h">
            <b>История платежей</b>
          </div>
          {hist.length ? (
            <div className="tblwrap bc-tbl">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th className="bh-pl">Назначение</th>
                    <th className="r">Сумма</th>
                    <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {hist.map((p) => (
                    <tr key={p.id}>
                      <td className="nw">{dlong(dateOf(p.paidAt ?? p.createdAt)!)}</td>
                      <td className="bh-pl">Подписка на месяц · {planOf(p.plan).n}</td>
                      <td className="r nw">
                        {rub(p.amount + (p.balance || 0))}
                        {p.balance ? <div className="sub">{p.amount ? `${rub(p.balance)} с баланса` : 'с баланса аккаунта'}</div> : null}
                      </td>
                      <td>
                        <span className="chip ok">оплачен</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bc-s">Платежей пока не было</div>
          )}
        </div>
      </div>

      {modal === 'plan' && <PlanModal me={me} onClose={() => setModal(null)} />}
      {modal === 'pay' && sub.pay && <PayModal me={me} onClose={() => setModal(null)} onPlan={() => setModal('plan')} />}
      {modal === 'pay' && !sub.pay && (
        <Modal
          title={ended ? 'Продлить подписку' : 'Оплата подписки'}
          onClose={() => setModal(null)}
          foot={
            <>
              <span className="sp"></span>
              <button className="btn gh" onClick={() => setModal(null)}>
                Закрыть
              </button>
              <button
                className="btn"
                onClick={() => {
                  setModal(null);
                  openSupport();
                }}
              >
                Написать в поддержку
              </button>
            </>
          }
        >
          <p className="lead" style={{ marginTop: 0 }}>
            Оплата картой появится совсем скоро. Пока подписку оплачивают через поддержку: напишите нам — пришлём счёт и{' '}
            {trial && !ended ? 'платная подписка начнётся сразу после пробного периода.' : 'сразу откроем кабинет.'}
          </p>
          <div className="hint">
            {pl.n} — {rub(pl.price)} в месяц.
          </div>
        </Modal>
      )}
      {modal === 'cancel' && (
        <Modal
          title="Отменить подписку"
          onClose={() => setModal(null)}
          foot={
            <>
              <span className="sp"></span>
              <button className="btn gh" onClick={() => setModal(null)}>
                Оставить подписку
              </button>
              <button
                className="btn rm"
                onClick={() =>
                  setSub.mutate(
                    { canceled: true },
                    {
                      onSuccess: () => {
                        setModal(null);
                        toast('Подписка отменена — доступ до ' + dlong(until));
                      },
                    },
                  )
                }
              >
                Отменить
              </button>
            </>
          }
        >
          <p className="lead">
            Доступ сохранится до <b>{dlong(until)}</b>, потом кабинет станет только для чтения: клиенты увидят уже назначенные тренировки, новые назначить будет нельзя.
          </p>
        </Modal>
      )}
    </>
  );
}

/* Оплата месяца подписки картой (CloudPayments): сохранённой картой — сразу, другой — через форму CloudPayments.
   Карта сохраняется для автопродления (services/billing.ts на сервере). */
function PayModal({ me, onClose, onPlan }: { me: Me; onClose: () => void; onPlan: () => void }) {
  const { pay, busy } = usePay();
  const sub = me.sub;
  const pl = planOf(sub.next || (sub.trial ? 'c5' : me.plan));
  const when = sub.ended
    ? 'Доступ откроется сразу после оплаты.'
    : sub.trial
      ? `Платная подписка начнётся после пробного периода — ${dlong(sub.paidUntil || me.planUntil || today())}.`
      : `Месяц добавится к оплаченному сроку — сейчас до ${dlong(me.planUntil || today())}.`;
  const go = (card: boolean) =>
    pay({ kind: 'month', plan: pl.id, card }).then((r) => {
      if (r.ok) {
        toast('Оплачено — спасибо!');
        onClose();
      } else if (r.msg) toast(r.msg);
    });
  return (
    <Modal
      title={sub.ended ? 'Продлить подписку' : 'Оплата подписки'}
      onClose={onClose}
      foot={
        <>
          <span className="sp"></span>
          {sub.saved && me.card ? (
            <>
              <button className="btn gh" disabled={busy} onClick={() => go(false)}>
                Другой картой
              </button>
              <button className="btn" id="mdPay" disabled={busy} onClick={() => go(true)}>
                Оплатить {rub(pl.price)} · {me.card}
              </button>
            </>
          ) : (
            <>
              <button className="btn gh" onClick={onClose}>
                Отмена
              </button>
              <button className="btn" id="mdPay" disabled={busy} onClick={() => go(false)}>
                Оплатить {rub(pl.price)}
              </button>
            </>
          )}
        </>
      }
    >
      <p className="lead" style={{ marginTop: 0 }}>
        <b>{pl.n}</b> — {rub(pl.price)} в месяц.{' '}
        <button className="lnk-t" onClick={onPlan}>
          Изменить тариф
        </button>
      </p>
      <p className="lead">{when}</p>
      <div className="hint">Карта сохранится для автопродления: {rub(pl.price)} раз в месяц, пока вы не отмените подписку в этом разделе. Платёж принимает CloudPayments, данные карты нам не передаются.</div>
    </Modal>
  );
}

/* Смена тарифа — как блок цен на лендинге: ступень выбирается ползунком по числу клиентов. Сразу стоит текущая,
   при перерасходе — первая, куда помещаются все клиенты. Лимит меняется сразу, цена — со следующего платежа;
   в пробный период ступень начнёт действовать после него. */
function PlanModal({ me, onClose }: { me: Me; onClose: () => void }) {
  const utils = trpc.useUtils();
  const nav = useNavigate();
  const m = trpc.trainer.setPlan.useMutation();
  const { pay, busy } = usePay();
  const trial = me.sub.trial;
  /* С оплатой картой повышение посреди оплаченного месяца — с доплатой за оставшиеся дни, понижение — со следующего платежа */
  const paid = me.sub.pay && !trial && !me.sub.ended;
  const used = me.used;
  const cur = PLANS.findIndex((x) => x.id === (trial ? me.sub.next : me.plan));
  const fit = PLANS.findIndex((x) => x.limit >= used);
  const [i, setI] = useState(cur >= 0 && used <= PLANS[cur].limit ? cur : fit >= 0 ? fit : PLANS.length - 1);
  const x = PLANS[i];
  const same = i === cur && !(paid && me.sub.next);
  const short = used > x.limit;
  const until = me.planUntil ? dlong(me.planUntil) : '';
  const up = paid && cur >= 0 && x.price > PLANS[cur].price;
  const quote = trpc.trainer.upQuote.useQuery({ plan: x.id }, { enabled: up });
  const extra = up ? quote.data?.amount : undefined;
  const payUp = () =>
    pay({ kind: 'up', plan: x.id, card: me.sub.saved }).then((r) => {
      if (r.ok) {
        utils.trainer.clients.invalidate();
        toast('Тариф изменён: ' + x.n);
        onClose();
        nav('/profile#billing', { replace: true });
      } else if (r.msg) toast(r.msg);
    });
  return (
    <Modal
      title="Тариф"
      onClose={onClose}
      foot={
        <>
          <span className="sp"></span>
          <button className="btn gh" onClick={onClose}>
            Отмена
          </button>
          <button
            className="btn"
            id="mdPlan"
            disabled={same || m.isPending || busy || (up && extra === undefined)}
            onClick={() =>
              up
                ? payUp()
                : m.mutate(
                { plan: x.id },
                {
                  onSuccess: () => {
                    utils.trainer.me.invalidate();
                    utils.trainer.clients.invalidate();
                    toast((trial ? 'После пробного периода — ' : paid ? (i === cur ? 'Тариф остаётся: ' : 'Со следующего платежа — ') : 'Тариф изменён: ') + x.n);
                    onClose();
                    nav('/profile#billing', { replace: true });
                  },
                  onError: (e) => toast(e.message || 'Не получилось'),
                },
              )
            }
          >
            {same
              ? 'Текущий тариф'
              : up
                ? extra === undefined
                  ? 'Считаем доплату…'
                  : `Доплатить ${rub(extra)}${me.sub.saved && me.card ? ' · ' + me.card : ''}`
                : paid && i === cur
                  ? 'Оставить текущий'
                  : (trial ? 'Выбрать за ' : 'Перейти на ') + rub(x.price) + ' в месяц'}
          </button>
        </>
      }
    >
      <p className="lead">Все возможности на любой ступени — цена зависит только от числа клиентов.</p>
      <div className="tpick">
        <div className="tp-top">
          <div className="tp-price">
            <b id="tpPrice">{rub(x.price)}</b>
            <s>/ месяц</s>
          </div>
          <span className="tp-lim" id="tpLim">
            {x.n}
          </span>
        </div>
        <input
          type="range"
          id="tpRange"
          min={0}
          max={PLANS.length - 1}
          step={1}
          value={i}
          aria-label="Количество клиентов"
          style={{ ['--p' as string]: (i / (PLANS.length - 1)) * 100 + '%' }}
          onChange={(e) => setI(+e.target.value)}
        />
        <div className="tp-ticks">
          {PLANS.map((p, k) => (
            <button key={p.id} type="button" className={k === i ? 'on' : ''} onClick={() => setI(k)}>
              {p.t}
            </button>
          ))}
        </div>
      </div>
      <div className="hint" id="tpNote">
        {same ? (
          trial ? (
            'Эта ступень начнёт действовать после пробного периода.'
          ) : (
            'Это ваш текущий тариф.'
          )
        ) : short ? (
          <span className="warn">
            У вас {used} {plural(used, 'клиент', 'клиента', 'клиентов')} — {used - x.limit} {plural(used - x.limit, 'окажется', 'окажутся', 'окажутся')} сверх лимита.
          </span>
        ) : trial ? (
          `После пробного периода — ${rub(x.price)} в месяц${until ? ', с ' + until : ''}. До его конца клиентов без ограничения.`
        ) : up ? (
          `Лимит изменится сразу. Доплата${extra !== undefined ? ' ' + rub(extra) : ''} — за оставшиеся дни до ${until}, дальше — ${rub(x.price)} в месяц.`
        ) : paid ? (
          i === cur ? `Запланированная смена тарифа отменится — со следующего платежа останется ${x.n}.` : `До ${until} действует оплаченный тариф, ${x.n} — со следующего платежа.`
        ) : (
          `Лимит изменится сразу, новая цена — со следующего платежа${until ? ', ' + until : ''}.`
        )}
      </div>
    </Modal>
  );
}
